import React, { useState, useEffect, useRef, useCallback } from 'react';
import { propertyService } from '../services/PropertyService';

// ── Theme tokens (match App.js dark shell) ────────────────────────────────────
const T = {
  bg:        '#0d0f1a',
  surface:   'rgba(255,255,255,0.03)',
  surfaceHi: 'rgba(255,255,255,0.06)',
  border:    'rgba(255,255,255,0.07)',
  borderHi:  'rgba(255,255,255,0.14)',
  gold:      '#c8a03c',
  goldDim:   'rgba(200,160,60,0.15)',
  goldBorder:'rgba(200,160,60,0.35)',
  text:      '#e2e8f0',
  muted:     '#94a3b8',
  dim:       '#475569',
  error:     '#ef4444',
  errorBg:   'rgba(239,68,68,0.10)',
  errorBdr:  'rgba(239,68,68,0.30)',
  success:   '#22c55e',
  successBg: 'rgba(34,197,94,0.10)',
  successBdr:'rgba(34,197,94,0.30)',
  warn:      '#f59e0b',
  font:      "'Tajawal','Arial',sans-serif",
  radius:    10,
};

const STATUS_CFG = {
  available: { label: 'متاح',       color: '#22c55e', bg: 'rgba(34,197,94,0.12)'   },
  booked:    { label: 'محجوز',      color: '#ef4444', bg: 'rgba(239,68,68,0.12)'   },
  reserved:  { label: 'قيد الحجز', color: '#f59e0b', bg: 'rgba(245,158,11,0.12)'  },
};

const AMENITY_OPTIONS = [
  'majlis','maidRoom','driverRoom','garden','pool',
  'smart_home','central_ac','elevator','covered_parking',
  'kitchen_fitted','gym','security_24_7',
];

const AMENITY_LABELS = {
  majlis:'مجلس', maidRoom:'غرفة خادمة', driverRoom:'غرفة سائق',
  garden:'حديقة', pool:'مسبح', smart_home:'منزل ذكي',
  central_ac:'تكييف مركزي', elevator:'مصعد', covered_parking:'موقف مغطى',
  kitchen_fitted:'مطبخ مجهّز', gym:'صالة رياضية', security_24_7:'أمن 24/7',
};

const EMPTY_FORM = {
  slug:'', title_en:'', title_ar:'',
  description_en:'', description_ar:'',
  short_description_en:'', short_description_ar:'',
  type:'villa', status:'available',
  district_en:'', district_ar:'',
  city_en:'Riyadh', city_ar:'الرياض',
  price_amount:'', price_currency:'SAR', price_period:'',
  area:'', bedrooms:'', bathrooms:'', parking:'', year_built:'',
  furnished:false, amenities:[],
  latitude:'', longitude:'', google_maps_url:'',
  permit_number:'', featured:false, is_published:true, display_order:0,
};

const REQUIRED = ['slug','title_en','title_ar','type','status'];

// ── Tiny shared components ────────────────────────────────────────────────────

function Notification({ note, onDismiss }) {
  if (!note) return null;
  const isErr = note.type === 'error';
  return (
    <div style={{
      display:'flex', alignItems:'center', justifyContent:'space-between',
      padding:'12px 16px', borderRadius:T.radius, marginBottom:16,
      background: isErr ? T.errorBg : T.successBg,
      border:`1px solid ${isErr ? T.errorBdr : T.successBdr}`,
      color: isErr ? T.error : T.success,
      fontFamily:T.font, fontSize:14,
    }}>
      <span>{note.message}</span>
      <button onClick={onDismiss} style={{background:'none',border:'none',color:'inherit',cursor:'pointer',fontSize:18,lineHeight:1,padding:'0 0 0 12px'}}>×</button>
    </div>
  );
}

function ConfirmModal({ modal, onClose }) {
  if (!modal) return null;
  return (
    <div style={{
      position:'fixed', inset:0, background:'rgba(0,0,0,0.7)',
      display:'flex', alignItems:'center', justifyContent:'center',
      zIndex:1000, fontFamily:T.font,
    }}>
      <div style={{
        background:'#131826', border:`1px solid ${T.borderHi}`,
        borderRadius:14, padding:'28px 32px', maxWidth:400, width:'90%',
      }}>
        <p style={{color:T.text, fontSize:15, margin:'0 0 24px', lineHeight:1.6}}>{modal.message}</p>
        <div style={{display:'flex', gap:10, justifyContent:'flex-end'}}>
          <button onClick={onClose} style={btnStyle('secondary')}>إلغاء</button>
          <button
            onClick={() => { modal.onConfirm(); onClose(); }}
            style={btnStyle(modal.danger ? 'danger' : 'primary')}
          >
            {modal.confirmLabel || 'تأكيد'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, required, error, children, fullWidth }) {
  return (
    <div style={{ display:'flex', flexDirection:'column', gap:6, gridColumn: fullWidth ? '1 / -1' : undefined }}>
      <label style={{ fontSize:12, color: error ? T.error : T.muted, fontFamily:T.font, fontWeight:600 }}>
        {label}{required && <span style={{color:T.error}}> *</span>}
      </label>
      {children}
      {error && <span style={{fontSize:11, color:T.error}}>{error}</span>}
    </div>
  );
}

function inputStyle(hasError) {
  return {
    background: hasError ? 'rgba(239,68,68,0.07)' : 'rgba(255,255,255,0.05)',
    border: `1px solid ${hasError ? T.errorBdr : T.border}`,
    borderRadius:8, padding:'9px 12px',
    color:T.text, fontFamily:T.font, fontSize:14,
    outline:'none', width:'100%', boxSizing:'border-box',
    transition:'border-color .15s',
  };
}

function btnStyle(variant) {
  const base = { border:'none', borderRadius:8, padding:'9px 20px', fontFamily:T.font, fontSize:13, fontWeight:700, cursor:'pointer', transition:'opacity .15s' };
  if (variant === 'primary')   return { ...base, background:`linear-gradient(135deg,${T.gold},#a07828)`, color:'#0d0f1a' };
  if (variant === 'secondary') return { ...base, background:'rgba(255,255,255,0.07)', color:T.muted, border:`1px solid ${T.border}` };
  if (variant === 'danger')    return { ...base, background:'rgba(239,68,68,0.15)', color:T.error, border:`1px solid ${T.errorBdr}` };
  if (variant === 'ghost')     return { ...base, background:'none', color:T.muted, padding:'6px 12px', fontSize:12 };
  return base;
}

// ── Main component ────────────────────────────────────────────────────────────

export function PropertyManagementTab({ user, role }) {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [saving, setSaving]         = useState(false);
  const [showForm, setShowForm]     = useState(false);
  const [editingId, setEditingId]   = useState(null);
  const [formData, setFormData]     = useState(EMPTY_FORM);
  const [touched, setTouched]       = useState({});
  const [notification, setNotification] = useState(null);
  const [modal, setModal]           = useState(null);

  // Images
  const [images, setImages]         = useState([]);
  const [imgUploading, setImgUploading] = useState(false);
  const [pendingFile, setPendingFile]   = useState(null); // { file, preview }
  const [altEn, setAltEn]           = useState('');
  const [altAr, setAltAr]           = useState('');
  const fileInputRef                = useRef(null);

  const notify = useCallback((type, message) => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 4000);
  }, []);

  const confirm = useCallback((message, onConfirm, { danger = false, confirmLabel } = {}) => {
    setModal({ message, onConfirm, danger, confirmLabel });
  }, []);

  useEffect(() => { fetchProperties(); }, []);

  useEffect(() => {
    if (!editingId) { setImages([]); return; }
    propertyService.getPropertyById(editingId).then((prop) => {
      if (prop?.property_images) {
        setImages([...prop.property_images].sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0)));
      }
    });
  }, [editingId]);

  async function fetchProperties() {
    try {
      setLoading(true);
      setProperties(await propertyService.getAllProperties());
    } catch {
      notify('error', 'فشل تحميل العقارات');
    } finally {
      setLoading(false);
    }
  }

  // ── Validation ──────────────────────────────────────────────────────────────
  function getErrors(data) {
    const errs = {};
    REQUIRED.forEach(f => { if (!data[f]) errs[f] = 'هذا الحقل مطلوب'; });
    return errs;
  }

  function touch(name) {
    setTouched(t => ({ ...t, [name]: true }));
  }

  function touchAll() {
    const all = {};
    REQUIRED.forEach(f => { all[f] = true; });
    setTouched(all);
  }

  const errors  = getErrors(formData);
  const isValid = Object.keys(errors).length === 0;

  // ── Form handlers ───────────────────────────────────────────────────────────
  function handleChange(e) {
    const { name, value, type, checked } = e.target;
    setFormData(f => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
  }

  function toggleAmenity(a) {
    setFormData(f => ({
      ...f,
      amenities: f.amenities.includes(a) ? f.amenities.filter(x => x !== a) : [...f.amenities, a],
    }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    touchAll();
    if (!isValid) { notify('error', 'يرجى تعبئة الحقول المطلوبة'); return; }
    try {
      setSaving(true);
      if (editingId) {
        await propertyService.updateProperty(editingId, formData, user.id);
        notify('success', 'تم تحديث العقار بنجاح');
      } else {
        await propertyService.createProperty(formData, user.id);
        notify('success', 'تم إنشاء العقار — افتح التعديل لإضافة الصور');
      }
      resetForm();
      await fetchProperties();
    } catch (err) {
      notify('error', `فشل الحفظ: ${err.message}`);
    } finally {
      setSaving(false);
    }
  }

  function handleEdit(prop) {
    setFormData({
      ...EMPTY_FORM, ...prop,
      price_amount:  prop.price_amount  ?? '',
      price_period:  prop.price_period  ?? '',
      area:          prop.area          ?? '',
      bedrooms:      prop.bedrooms      ?? '',
      bathrooms:     prop.bathrooms     ?? '',
      parking:       prop.parking       ?? '',
      year_built:    prop.year_built    ?? '',
      latitude:      prop.latitude      ?? '',
      longitude:     prop.longitude     ?? '',
      amenities:     Array.isArray(prop.amenities) ? prop.amenities : [],
    });
    setEditingId(prop.id);
    setTouched({});
    setShowForm(true);
  }

  function handleDelete(id, titleEn) {
    confirm(
      `هل أنت متأكد من حذف "${titleEn}"؟ لا يمكن التراجع عن هذا الإجراء.`,
      async () => {
        try {
          setLoading(true);
          await propertyService.deleteProperty(id);
          notify('success', 'تم حذف العقار');
          await fetchProperties();
        } catch {
          notify('error', 'فشل حذف العقار');
        } finally {
          setLoading(false);
        }
      },
      { danger: true, confirmLabel: 'حذف' },
    );
  }

  function resetForm() {
    setShowForm(false);
    setEditingId(null);
    setFormData(EMPTY_FORM);
    setTouched({});
    setPendingFile(null);
    setAltEn('');
    setAltAr('');
    setImages([]);
  }

  // ── Image handlers ──────────────────────────────────────────────────────────
  function handleFileSelect(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    const preview = URL.createObjectURL(file);
    setPendingFile({ file, preview });
    setAltEn('');
    setAltAr('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  async function handleImageUpload() {
    if (!pendingFile || !editingId) return;
    try {
      setImgUploading(true);
      const img = await propertyService.uploadPropertyImage(editingId, pendingFile.file, altEn, altAr);
      setImages(prev => [...prev, img]);
      URL.revokeObjectURL(pendingFile.preview);
      setPendingFile(null);
      setAltEn('');
      setAltAr('');
      notify('success', 'تم رفع الصورة');
    } catch {
      notify('error', 'فشل رفع الصورة');
    } finally {
      setImgUploading(false);
    }
  }

  function handleCancelUpload() {
    if (pendingFile) URL.revokeObjectURL(pendingFile.preview);
    setPendingFile(null);
    setAltEn('');
    setAltAr('');
  }

  function handleImageDelete(imageId, imageUrl) {
    confirm(
      'هل تريد حذف هذه الصورة؟',
      async () => {
        try {
          const path = imageUrl.split('/property-images/')[1];
          await propertyService.deletePropertyImage(imageId, path);
          setImages(prev => prev.filter(img => img.id !== imageId));
          notify('success', 'تم حذف الصورة');
        } catch {
          notify('error', 'فشل حذف الصورة');
        }
      },
      { danger: true, confirmLabel: 'حذف' },
    );
  }

  // ── Render helpers ──────────────────────────────────────────────────────────
  function inp(name, extraStyle = {}) {
    const hasErr = touched[name] && errors[name];
    return {
      style: { ...inputStyle(hasErr), ...extraStyle },
      name, value: formData[name] ?? '',
      onChange: handleChange,
      onBlur: () => touch(name),
    };
  }

  function errMsg(name) {
    return touched[name] && errors[name] ? errors[name] : null;
  }

  const sectionStyle = {
    background: T.surface,
    border: `1px solid ${T.border}`,
    borderRadius: T.radius + 2,
    padding: '20px 20px 8px',
    marginBottom: 16,
  };

  const sectionTitle = {
    color: T.gold,
    fontSize: 13,
    fontWeight: 700,
    fontFamily: T.font,
    margin: '0 0 16px',
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
  };

  const gridRow = { display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(220px,1fr))', gap:14, marginBottom:14 };

  // ── Loading ─────────────────────────────────────────────────────────────────
  if (loading && !showForm) {
    return (
      <div style={{ padding:60, textAlign:'center', color:T.muted, fontFamily:T.font }}>
        جاري التحميل…
      </div>
    );
  }

  // ── List view ───────────────────────────────────────────────────────────────
  if (!showForm) return (
    <div style={{ padding:'24px 20px', fontFamily:T.font, color:T.text, minHeight:'100vh', background:T.bg }}>
      <ConfirmModal modal={modal} onClose={() => setModal(null)} />
      <Notification note={notification} onDismiss={() => setNotification(null)} />

      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:20 }}>
        <div>
          <h2 style={{ margin:0, fontSize:18, color:T.gold, fontWeight:800 }}>🏘️ إدارة العقارات</h2>
          <p style={{ margin:'4px 0 0', fontSize:12, color:T.dim }}>
            الدور: <span style={{ color: role === 'admin' ? T.gold : T.muted }}>{role === 'admin' ? 'مدير (صلاحيات كاملة)' : 'موظف'}</span>
          </p>
        </div>
        <button onClick={() => setShowForm(true)} style={btnStyle('primary')}>+ إضافة عقار</button>
      </div>

      {properties.length === 0 ? (
        <div style={{ textAlign:'center', padding:'60px 20px', color:T.dim, background:T.surface, border:`1px solid ${T.border}`, borderRadius:T.radius }}>
          لا توجد عقارات. أضف أول عقار الآن.
        </div>
      ) : (
        <div style={{ overflowX:'auto' }}>
          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
            <thead>
              <tr style={{ borderBottom:`1px solid ${T.border}` }}>
                {['العنوان','النوع','الحالة','منشور','السعر','المساحة',''].map(h => (
                  <th key={h} style={{ padding:'10px 14px', textAlign:'right', color:T.muted, fontWeight:600, whiteSpace:'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {properties.map(prop => {
                const sc = STATUS_CFG[prop.status] || STATUS_CFG.available;
                return (
                  <tr key={prop.id} style={{ borderBottom:`1px solid ${T.border}`, transition:'background .12s' }}
                    onMouseEnter={e => e.currentTarget.style.background = T.surfaceHi}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <td style={{ padding:'12px 14px' }}>
                      <div style={{ color:T.text, fontWeight:600 }}>{prop.title_ar}</div>
                      <div style={{ color:T.dim, fontSize:11, marginTop:2 }}>{prop.title_en}</div>
                    </td>
                    <td style={{ padding:'12px 14px', color:T.muted }}>{prop.type}</td>
                    <td style={{ padding:'12px 14px' }}>
                      <span style={{ display:'inline-block', padding:'3px 10px', borderRadius:20, fontSize:12, fontWeight:700, background:sc.bg, color:sc.color }}>
                        {sc.label}
                      </span>
                    </td>
                    <td style={{ padding:'12px 14px', fontSize:16, textAlign:'center' }}>{prop.is_published ? '✅' : '🔴'}</td>
                    <td style={{ padding:'12px 14px', color:T.muted }}>{prop.price_amount ? prop.price_amount.toLocaleString() + ' ر.س' : '—'}</td>
                    <td style={{ padding:'12px 14px', color:T.muted }}>{prop.area ? `${prop.area} م²` : '—'}</td>
                    <td style={{ padding:'12px 14px', whiteSpace:'nowrap' }}>
                      <button onClick={() => handleEdit(prop)} style={{ ...btnStyle('ghost'), marginLeft:4 }}>تعديل</button>
                      {role === 'admin' && (
                        <button onClick={() => handleDelete(prop.id, prop.title_en)} style={{ ...btnStyle('ghost'), color:T.error }}>حذف</button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  // ── Form view ────────────────────────────────────────────────────────────────
  return (
    <div style={{ padding:'24px 20px', fontFamily:T.font, color:T.text, background:T.bg, minHeight:'100vh' }}>
      <ConfirmModal modal={modal} onClose={() => setModal(null)} />

      {/* Header */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:20 }}>
        <div>
          <button onClick={resetForm} style={{ ...btnStyle('ghost'), padding:'4px 0', marginBottom:6, display:'flex', alignItems:'center', gap:6 }}>
            ← العودة للقائمة
          </button>
          <h2 style={{ margin:0, fontSize:17, color:T.gold, fontWeight:800 }}>
            {editingId ? 'تعديل العقار' : 'إضافة عقار جديد'}
          </h2>
        </div>
      </div>

      <Notification note={notification} onDismiss={() => setNotification(null)} />

      <form onSubmit={handleSubmit} noValidate>

        {/* ── Basic Info ─────────────────────────────────────────────────────── */}
        <div style={sectionStyle}>
          <p style={sectionTitle}>المعلومات الأساسية</p>
          <div style={gridRow}>
            <Field label="Slug (رابط العقار)" required error={errMsg('slug')}>
              <input {...inp('slug')} placeholder="al-arid-modern-villa" dir="ltr" />
            </Field>
            <Field label="النوع" required error={errMsg('type')}>
              <select {...inp('type')} style={inputStyle(touched.type && errors.type)}>
                <option value="villa">فيلا</option>
                <option value="apartment">شقة</option>
                <option value="land">أرض</option>
                <option value="commercial">تجاري</option>
                <option value="townhouse">تاون هاوس</option>
              </select>
            </Field>
            <Field label="الحالة" required error={errMsg('status')}>
              <select {...inp('status')} style={inputStyle(touched.status && errors.status)}>
                <option value="available">متاح ✅</option>
                <option value="booked">محجوز 🔴</option>
                <option value="reserved">قيد الحجز ⏳</option>
              </select>
            </Field>
          </div>

          <div style={gridRow}>
            <Field label="العنوان (عربي)" required error={errMsg('title_ar')}>
              <input {...inp('title_ar')} placeholder="فيلا حديثة في حي العارض" dir="rtl" />
            </Field>
            <Field label="Title (English)" required error={errMsg('title_en')}>
              <input {...inp('title_en')} placeholder="Modern Villa in Al Arid" dir="ltr" />
            </Field>
          </div>

          <div style={gridRow}>
            <Field label="وصف مختصر (عربي)">
              <input {...inp('short_description_ar')} placeholder="جملة موجزة للبطاقة" dir="rtl" />
            </Field>
            <Field label="Short Description (English)">
              <input {...inp('short_description_en')} placeholder="One-line summary for cards" dir="ltr" />
            </Field>
          </div>

          <div style={gridRow}>
            <Field label="الوصف التفصيلي (عربي)">
              <textarea {...inp('description_ar')} placeholder="وصف تفصيلي…" rows={3} dir="rtl" style={{ ...inputStyle(false), resize:'vertical' }} />
            </Field>
            <Field label="Description (English)">
              <textarea {...inp('description_en')} placeholder="Full description…" rows={3} dir="ltr" style={{ ...inputStyle(false), resize:'vertical' }} />
            </Field>
          </div>

          <div style={gridRow}>
            <Field label="رقم التصريح الإعلان العقاري">
              <input {...inp('permit_number')} placeholder="1234567890" dir="ltr" />
            </Field>
            <Field label="ترتيب العرض">
              <input {...inp('display_order')} type="number" placeholder="0" />
            </Field>
          </div>

          <div style={{ display:'flex', gap:24, flexWrap:'wrap', marginBottom:8 }}>
            {[
              { name:'featured',    label:'مميز على الصفحة الرئيسية' },
              { name:'is_published',label:'منشور (مرئي على الموقع)'  },
              { name:'furnished',   label:'مفروش'                     },
            ].map(({ name, label }) => (
              <label key={name} style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer', fontSize:13, color:T.muted }}>
                <input
                  type="checkbox"
                  name={name}
                  checked={!!formData[name]}
                  onChange={handleChange}
                  style={{ width:16, height:16, accentColor:T.gold, cursor:'pointer' }}
                />
                {label}
              </label>
            ))}
          </div>
        </div>

        {/* ── Location & Pricing ─────────────────────────────────────────────── */}
        <div style={sectionStyle}>
          <p style={sectionTitle}>الموقع والتسعير</p>
          <div style={gridRow}>
            <Field label="الحي (عربي)">
              <input {...inp('district_ar')} placeholder="حي العارض" dir="rtl" />
            </Field>
            <Field label="District (English)">
              <input {...inp('district_en')} placeholder="Al Arid" dir="ltr" />
            </Field>
            <Field label="المدينة (عربي)">
              <input {...inp('city_ar')} placeholder="الرياض" dir="rtl" />
            </Field>
            <Field label="City (English)">
              <input {...inp('city_en')} placeholder="Riyadh" dir="ltr" />
            </Field>
          </div>
          <div style={gridRow}>
            <Field label="خط العرض (Latitude)">
              <input {...inp('latitude')} type="number" step="0.0001" placeholder="24.9215" dir="ltr" />
            </Field>
            <Field label="خط الطول (Longitude)">
              <input {...inp('longitude')} type="number" step="0.0001" placeholder="46.6724" dir="ltr" />
            </Field>
          </div>
          <div style={{ marginBottom:14 }}>
            <Field label="رابط خرائط جوجل" fullWidth>
              <input {...inp('google_maps_url')} type="url" placeholder="https://maps.google.com/?q=24.9215,46.6724" dir="ltr" />
            </Field>
          </div>
          <div style={gridRow}>
            <Field label="السعر (ر.س)">
              <input {...inp('price_amount')} type="number" placeholder="2850000" dir="ltr" />
            </Field>
            <Field label="فترة السعر">
              <select {...inp('price_period')} style={inputStyle(false)}>
                <option value="">إجمالي (بدون فترة)</option>
                <option value="month">شهرياً</option>
                <option value="year">سنوياً</option>
              </select>
            </Field>
          </div>
        </div>

        {/* ── Property Details ───────────────────────────────────────────────── */}
        <div style={sectionStyle}>
          <p style={sectionTitle}>تفاصيل العقار</p>
          <div style={gridRow}>
            <Field label="المساحة (م²)"><input {...inp('area')} type="number" placeholder="420" /></Field>
            <Field label="غرف النوم"><input {...inp('bedrooms')} type="number" placeholder="5" /></Field>
            <Field label="الحمامات"><input {...inp('bathrooms')} type="number" placeholder="6" /></Field>
            <Field label="المواقف"><input {...inp('parking')} type="number" placeholder="2" /></Field>
            <Field label="سنة البناء"><input {...inp('year_built')} type="number" placeholder="2024" /></Field>
          </div>
        </div>

        {/* ── Amenities ──────────────────────────────────────────────────────── */}
        <div style={sectionStyle}>
          <p style={sectionTitle}>المميزات</p>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(160px,1fr))', gap:10 }}>
            {AMENITY_OPTIONS.map(a => {
              const active = formData.amenities.includes(a);
              return (
                <label key={a} style={{
                  display:'flex', alignItems:'center', gap:8, cursor:'pointer',
                  padding:'8px 12px', borderRadius:8, fontSize:13,
                  background: active ? T.goldDim : T.surface,
                  border: `1px solid ${active ? T.goldBorder : T.border}`,
                  color: active ? T.gold : T.muted,
                  transition:'all .15s',
                }}>
                  <input
                    type="checkbox"
                    checked={active}
                    onChange={() => toggleAmenity(a)}
                    style={{ display:'none' }}
                  />
                  {active ? '✓ ' : ''}{AMENITY_LABELS[a] || a}
                </label>
              );
            })}
          </div>
        </div>

        {/* ── Images (edit mode only) ────────────────────────────────────────── */}
        {editingId && (
          <div style={sectionStyle}>
            <p style={sectionTitle}>الصور</p>

            {/* Existing images */}
            <div style={{ display:'flex', flexWrap:'wrap', gap:12, marginBottom:20 }}>
              {images.map(img => (
                <div key={img.id} style={{
                  position:'relative', width:130, borderRadius:8,
                  overflow:'hidden', border:`1px solid ${T.border}`,
                  background: T.surface,
                }}>
                  <img src={img.image_url} alt={img.alt_text_en || ''} style={{ width:'100%', height:90, objectFit:'cover', display:'block' }} />
                  <div style={{ padding:'4px 8px', fontSize:11, color:T.dim, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
                    {img.alt_text_ar || img.alt_text_en || '—'}
                  </div>
                  <button
                    type="button"
                    onClick={() => handleImageDelete(img.id, img.image_url)}
                    style={{
                      position:'absolute', top:4, right:4,
                      background:'rgba(239,68,68,0.85)', color:'#fff',
                      border:'none', borderRadius:5, width:22, height:22,
                      fontSize:14, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center',
                    }}
                  >×</button>
                </div>
              ))}
              {images.length === 0 && !pendingFile && (
                <p style={{ color:T.dim, fontSize:13 }}>لا توجد صور بعد.</p>
              )}
            </div>

            {/* Pending upload form */}
            {pendingFile ? (
              <div style={{
                background:'rgba(255,255,255,0.03)', border:`1px solid ${T.borderHi}`,
                borderRadius:10, padding:16, maxWidth:460,
              }}>
                <p style={{ margin:'0 0 12px', fontSize:13, color:T.gold, fontWeight:700 }}>إضافة نص بديل للصورة</p>
                <img src={pendingFile.preview} alt="" style={{ width:'100%', maxHeight:160, objectFit:'cover', borderRadius:6, marginBottom:12 }} />
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:12 }}>
                  <Field label="النص البديل (عربي)">
                    <input
                      value={altAr}
                      onChange={e => setAltAr(e.target.value)}
                      placeholder="صورة خارجية للفيلا"
                      dir="rtl"
                      style={inputStyle(false)}
                    />
                  </Field>
                  <Field label="Alt Text (English)">
                    <input
                      value={altEn}
                      onChange={e => setAltEn(e.target.value)}
                      placeholder="Exterior view of villa"
                      dir="ltr"
                      style={inputStyle(false)}
                    />
                  </Field>
                </div>
                <div style={{ display:'flex', gap:8 }}>
                  <button type="button" onClick={handleImageUpload} style={btnStyle('primary')} disabled={imgUploading}>
                    {imgUploading ? 'جاري الرفع…' : 'رفع الصورة'}
                  </button>
                  <button type="button" onClick={handleCancelUpload} style={btnStyle('secondary')}>إلغاء</button>
                </div>
              </div>
            ) : (
              <div>
                <label htmlFor="img-upload" style={{
                  display:'inline-flex', alignItems:'center', gap:6,
                  padding:'8px 16px', borderRadius:8, cursor:'pointer', fontSize:13, fontWeight:700,
                  background: T.surfaceHi, border:`1px solid ${T.border}`, color:T.muted,
                  transition:'border-color .15s',
                }}>
                  + اختر صورة
                </label>
                <input
                  id="img-upload"
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileSelect}
                  style={{ display:'none' }}
                />
                <p style={{ marginTop:6, fontSize:11, color:T.dim }}>JPEG أو PNG — يُفضَّل عرض أكبر من 1200px</p>
              </div>
            )}
          </div>
        )}

        {!editingId && (
          <p style={{ fontSize:12, color:T.dim, marginBottom:16 }}>
            * يمكنك إضافة الصور بعد إنشاء العقار عن طريق الضغط على "تعديل".
          </p>
        )}

        {/* ── Actions ────────────────────────────────────────────────────────── */}
        <div style={{ display:'flex', gap:10, paddingTop:8 }}>
          <button type="submit" style={btnStyle('primary')} disabled={saving}>
            {saving ? 'جاري الحفظ…' : editingId ? 'حفظ التعديلات' : 'إنشاء العقار'}
          </button>
          <button type="button" onClick={resetForm} style={btnStyle('secondary')} disabled={saving}>
            إلغاء
          </button>
        </div>
      </form>
    </div>
  );
}
